// src/pages/ClaimDossier/index.tsx
import React, { useEffect, useState } from 'react';
import './ClaimDossier.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Modal, ModalBody, ModalFooter, Badge, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import BreadCrumb from 'Components/Common/BreadCrumb';
import ClaimDossierTable from './ClaimDossierTable';
import CreateClaimDossierModal from './modals/CreateClaimDossierModal';
import DossierDecisionModal from './modals/DossierDecisionModal';
import AgentPayoutInfoModal from './modals/AgentPayoutInfoModal';
import TicketQrScanControl from './components/TicketQrScanControl';
import { decodeScannedTicket, ScannedTicket } from 'helpers/ticketQrDecode';
import { decodeTicketToken } from 'helpers/fakebackend_helper';
import { decodeHtmlEntities } from 'helpers/htmlText';
import { AgentMap, buildAgentMap, loadAgentsWithCache } from 'helpers/agentLookup';
import {
  fetchClaimDossiers,
  fetchClaimDossierById,
  fetchAgentPayoutInfo,
  approveClaimDossier,
  cancelClaimDossier,
  appraiseClaimDossier,
  postAuditClaimDossier,
} from 'slices/claimDossier/thunk';
import { setStatusFilter, setSelectedDossier, openModal, closeModal } from 'slices/claimDossier/reducer';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import {
  PrizeClaimDossier,
  ClaimDossierReviewDecision,
  AgentPayoutInfo,
  ClaimDossierSource,
  ClaimDossierStatus,
  CLAIM_DOSSIER_STATUS_LABELS,
  CLAIM_DOSSIER_STATUS_BADGE,
  CLAIM_DOSSIER_PAYMENT_STATUS_LABELS,
  CLAIM_DOSSIER_PAYMENT_STATUS_BADGE,
  CLAIM_DOSSIER_REVIEW_DECISION_LABELS,
  CLAIM_DOSSIER_REVIEW_DECISION_BADGE,
} from './types';
import { useBrandingTitle } from '../../hooks';
import {
  useTaxConfig,
  formatTaxRate,
  formatTaxThreshold,
  computeWithholdingTax,
  isTaxRecomputable,
} from './useTaxConfig';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier');

// Mục 7.2 (README.md) — cùng 1 component phục vụ 2 menu:
//  - 'appraisal'  → /claim-dossiers        : Thẩm định Hồ sơ (mặc định, hành vi cũ)
//  - 'postAudit'  → /claim-dossier-post-audit : Hậu kiểm Trả thưởng (chỉ hồ sơ AgentPaid đã Thẩm định = Phê duyệt)
interface ClaimDossierPageProps {
  mode?: 'appraisal' | 'postAudit';
}

type ReviewStageFilter = 'ChoHauKiem' | 'DaHauKiem';

const REVIEW_STAGE_LABELS: Record<ReviewStageFilter, string> = {
  ChoHauKiem: st('cho_hau_kiem_text'),
  DaHauKiem: st('da_hau_kiem_text'),
};

// 2 tab màn Thẩm định hồ sơ — chia theo NGUỒN lập hồ sơ (BE lọc theo AgentId null / có giá trị):
//  - Company: hồ sơ do tài khoản admin công ty lập → "Trả thưởng trực tiếp"
//  - Agent  : hồ sơ do đại lý lập, admin cần duyệt & thẩm định lại → "Thẩm định trả thưởng"
const SOURCE_TABS: { value: ClaimDossierSource; label: string; icon: string }[] = [
  { value: 'Company', label: st('tab_tra_thuong_truc_tiep_text'), icon: 'ri-building-line' },
  { value: 'Agent', label: st('tab_tham_dinh_tra_thuong_text'), icon: 'ri-store-2-line' },
];

// Trạng thái lọc phù hợp từng tab (hồ sơ đại lý lập luôn ở AgentPaid, không qua Chờ duyệt/Ký duyệt).
const SOURCE_STATUS_OPTIONS: Record<ClaimDossierSource, ClaimDossierStatus[]> = {
  Company: ['PendingApproval', 'Verified', 'Cancelled'],
  Agent: ['AgentPaid', 'Cancelled'],
};

// Nội dung quét từ máy quét/dán link QR (URL hoặc token dài) — khác Số vé / CCCD (chỉ gồm chữ số).
const looksLikeQrContent = (value: string) => value.length >= 20 && !/^\d+$/.test(value);

// Khối ghi chú Thẩm định/Hậu kiểm — hiển thị đầy đủ, giữ xuống dòng, giải mã ký tự HTML của dữ liệu cũ.
const ReviewCommentBox: React.FC<{ comment?: string | null }> = ({ comment }) => {
  const text = decodeHtmlEntities(comment).trim();
  if (!text) return null;
  return (
    <div className="mt-1 p-2 rounded bg-light border fs-13" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      <span className="text-muted fw-semibold">{st('ghi_chu_text_2')}</span> {text}
    </div>
  );
};

const ClaimDossierPage: React.FC<ClaimDossierPageProps> = ({ mode = 'appraisal' }) => {
  const isPostAudit = mode === 'postAudit';

  useBrandingTitle(isPostAudit ? st('hau_kiem_tra_thuong_text') : st('tham_dinh_ho_so_text'));

  // Mục 7.1 — ngưỡng + tỷ lệ khấu trừ Thuế TNCN lấy từ system-service (không còn fix cứng 10tr/10%).
  const taxConfig = useTaxConfig();

  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { dossiers, loading, pagination, filters, modalState, selectedDossier, detailLoading, approvingId, cancellingId } =
    useSelector((state: any) => state.ClaimDossier);

  const [pendingCancel, setPendingCancel] = useState<PrizeClaimDossier | null>(null);
  const [scanValue, setScanValue] = useState('');
  // Mục 7.2 — bộ lọc riêng cho màn Hậu kiểm (không dùng chung filters.status trong redux).
  const [reviewStage, setReviewStage] = useState<ReviewStageFilter>('ChoHauKiem');
  // README.md muc 9 — ho so AgentPaid: buoc Tham dinh/Hau kiem dung chung 1 modal quyet dinh
  // (Phe duyet/Tu choi), phan biet nhau bang reviewType.
  const [pendingReview, setPendingReview] = useState<{ dossier: PrizeClaimDossier; reviewType: 'Appraisal' | 'PostAudit' } | null>(
    null
  );
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  // Bổ sung — modal "Thông tin trả thưởng đại lý" (đại lý nào trả, trực tiếp/chéo, lô vé + đại lý được phân).
  const [agentPayoutOpen, setAgentPayoutOpen] = useState(false);
  const [agentPayoutLoading, setAgentPayoutLoading] = useState(false);
  const [agentPayoutInfo, setAgentPayoutInfo] = useState<AgentPayoutInfo | null>(null);
  // Tài khoản ĐẠI LÝ: mọi hồ sơ của họ đều thuộc nguồn "Agent" (BE cũng chỉ trả hồ sơ do chính họ lập)
  // -> khoá cứng tab "Thẩm định trả thưởng", ẩn thanh tab, vẫn cho "Tạo hồ sơ KQ".
  const currentAgentId = useSelector((state: any) => state.UserProfile?.profile?.agentId);
  const isAgentAccount = !!currentAgentId;
  // Tab nguồn lập hồ sơ (chỉ màn Thẩm định; màn Hậu kiểm luôn là hồ sơ đại lý).
  const [selectedSource, setSource] = useState<ClaimDossierSource>('Company');
  const source: ClaimDossierSource = isAgentAccount ? 'Agent' : selectedSource;
  // Danh sách đại lý (cache 5 phút) — fallback hiển thị tên đại lý khi BE chưa enrich được.
  const [agentMap, setAgentMap] = useState<AgentMap>({});

  useEffect(() => {
    loadAgentsWithCache().then((agents) => setAgentMap(buildAgentMap(agents)));
  }, []);

  // Mục 7.2 — màn Hậu kiểm lọc theo ReviewStage (BE tự giới hạn Status = AgentPaid + đã Thẩm định
  // Phê duyệt); màn Thẩm định giữ nguyên lọc theo Status như cũ.
  const buildListParams = () => ({
    PageIndex: pagination.pageIndex,
    PageSize: pagination.pageSize,
    ...(isPostAudit ? { ReviewStage: reviewStage } : { Status: filters.status, Source: source }),
  });

  useEffect(() => {
    dispatch(fetchClaimDossiers(buildListParams()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pagination.pageIndex, filters.status, reviewStage, isPostAudit, source]);

  // Đổi tab: bỏ lọc trạng thái + ô tra cứu của tab cũ (setStatusFilter cũng đưa về trang 1).
  const handleSourceChange = (next: ClaimDossierSource) => {
    if (next === source) return;
    setScanValue('');
    dispatch(setStatusFilter(undefined));
    setSource(next);
  };

  const handleRefresh = () => dispatch(fetchClaimDossiers(buildListParams()));

  // FR-002: quét/nhập mã vé hoặc CCCD để tra cứu nhanh hồ sơ hiện có — hành vi "tự mở form Tạo Hồ sơ (autofill)"
  // chưa được xác nhận nghiệp vụ (xem .features/tham-dinh-ho-so/implementation-context-fe.md mục 8.2), tạm coi
  // input này là filter nhanh theo Số vé HOẶC CCCD dựa trên độ dài chuỗi nhập vào.
  const searchByTicketNumber = (ticketNumber: string) =>
    dispatch(
      fetchClaimDossiers({
        PageIndex: 1,
        PageSize: pagination.pageSize,
        TicketNumber: ticketNumber,
        Source: isPostAudit ? undefined : source,
      })
    );

  // Quét QR vé (camera / máy quét / dán link) -> giải mã ra số vé rồi tìm hồ sơ của vé đó.
  const handleTicketDecoded = (ticket: ScannedTicket) => {
    setScanValue(ticket.ticketNumber);
    toast.info(st('dang_tim_ho_so_cua_ve_message', { symbol: ticket.symbol, ticket_number: ticket.ticketNumber }));
    searchByTicketNumber(ticket.ticketNumber);
  };

  const handleScanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = scanValue.trim();
    if (!value) {
      handleRefresh();
      return;
    }
    // Máy quét cầm tay gõ thẳng nội dung QR (URL/token) vào ô này -> giải mã trước rồi mới tìm theo số vé.
    if (looksLikeQrContent(value)) {
      const decoded = await decodeScannedTicket(value, decodeTicketToken);
      if (!decoded) {
        toast.error(st('khong_doc_duoc_ma_qr_ve_message'));
        return;
      }
      handleTicketDecoded(decoded);
      return;
    }
    const isIdNumber = /^\d{9}$|^\d{12}$/.test(value);
    dispatch(
      fetchClaimDossiers({
        PageIndex: 1,
        PageSize: pagination.pageSize,
        ClaimantIdNumber: isIdNumber ? value : undefined,
        TicketNumber: isIdNumber ? undefined : value,
        Source: isPostAudit ? undefined : source,
      })
    );
  };

  const handleApprove = (dossier: PrizeClaimDossier) => {
    dispatch(approveClaimDossier(dossier.id)).then((action: any) => {
      if (approveClaimDossier.fulfilled.match(action)) handleRefresh();
    });
  };

  const handleConfirmCancel = () => {
    if (!pendingCancel) return;
    dispatch(cancelClaimDossier({ id: pendingCancel.id })).then((action: any) => {
      if (cancelClaimDossier.fulfilled.match(action)) handleRefresh();
    });
    setPendingCancel(null);
  };

  const handleSubmitReview = (decision: ClaimDossierReviewDecision, comment?: string) => {
    if (!pendingReview) return;
    const thunk = pendingReview.reviewType === 'Appraisal' ? appraiseClaimDossier : postAuditClaimDossier;
    setReviewSubmitting(true);
    dispatch(thunk({ id: pendingReview.dossier.id, decision, comment })).then((action: any) => {
      setReviewSubmitting(false);
      if (thunk.fulfilled.match(action)) {
        setPendingReview(null);
        handleRefresh();
      }
    });
  };

  // Danh sách phân trang KHÔNG trả về đủ breakdown từng giải (tránh chạy lại thuật toán chấm vé cho mỗi dòng)
  // nên phải gọi fetchClaimDossierById để lấy chi tiết đầy đủ (gồm "prizes"). Xóa selectedDossier cũ trước khi
  // mở modal để tránh hiển thị chớp nháy dữ liệu hồ sơ trước đó — modal chỉ render nội dung sau khi tải xong.
  const handleView = (dossier: PrizeClaimDossier) => {
    dispatch(setSelectedDossier(null));
    dispatch(openModal('detail'));
    dispatch(fetchClaimDossierById(dossier.id));
  };

  // Bổ sung — mở modal "Thông tin trả thưởng đại lý": xóa dữ liệu cũ, mở modal ở trạng thái loading,
  // rồi gọi payout-service lấy thông tin (đại lý trả thưởng, trực tiếp/chéo, lô vé + đại lý được phân).
  const handleViewAgentPayout = (dossier: PrizeClaimDossier) => {
    setAgentPayoutInfo(null);
    setAgentPayoutOpen(true);
    setAgentPayoutLoading(true);
    dispatch(fetchAgentPayoutInfo(dossier.id)).then((action: any) => {
      setAgentPayoutLoading(false);
      if (fetchAgentPayoutInfo.fulfilled.match(action)) {
        setAgentPayoutInfo(action.payload as AgentPayoutInfo);
      } else {
        setAgentPayoutOpen(false);
      }
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={isPostAudit ? st('hau_kiem_tra_thuong_text') : st('tham_dinh_ho_so_text')} pageTitle={st('tra_thuong_text')} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="card-header border-0">
                  <Row className="align-items-center gy-3">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">
                        {isPostAudit
                          ? st('hau_kiem_ket_qua_tra_thuong_text')
                          : st('tham_dinh_ho_so_tra_thuong_text', { value: formatTaxRate(taxConfig) })}
                      </h5>
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex gap-1 flex-wrap">
                        {!isPostAudit && (source === 'Company' || isAgentAccount) && PageActionPermission.canDisplay(location.pathname, 'button-add') && (
                          <Button
                            color="success"
                            data-action-code="button-add"
                            data-action-name="Tạo hồ sơ kết quả"
                            onClick={() => dispatch(openModal('create'))}
                          >
                            <i className="ri-add-line me-1"></i> {st('tao_ho_so_kq_button')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  {!isPostAudit && !isAgentAccount && (
                    <Nav tabs className="nav-tabs-custom nav-success mb-3">
                      {SOURCE_TABS.map((tab) => (
                        <NavItem key={tab.value}>
                          <NavLink
                            href="#"
                            className={classnames({ active: source === tab.value })}
                            onClick={(e) => {
                              e.preventDefault();
                              handleSourceChange(tab.value);
                            }}
                          >
                            <i className={`${tab.icon} me-1 align-bottom`}></i>
                            {tab.label}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  )}
                  <Row className="g-3 mb-5 align-items-end">
                    {!isPostAudit && (
                      <Col md={5}>
                        <Label className="form-label">{st('quet_tra_cuu_ma_ve_hoac_label')}</Label>
                        <form className="d-flex gap-2" onSubmit={handleScanSubmit}>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder={st('quet_qr_ma_ve_hoac_cccd_placeholder')}
                            data-action-code="input-scan-ticket-or-id"
                            data-action-name="Quét mã vé hoặc CCCD"
                            value={scanValue}
                            onChange={(e) => setScanValue(e.target.value)}
                            disabled={!PageActionPermission.canEnable(location.pathname, 'input-scan-ticket-or-id')}
                          />
                          {PageActionPermission.canDisplay(location.pathname, 'button-scan-submit') && (
                            <Button
                              type="submit"
                              color="soft-secondary"
                              data-action-code="button-scan-submit"
                              data-action-name="Tra cứu hồ sơ"
                            >
                              <i className="ri-search-line"></i>
                            </Button>
                          )}
                          {/* Quét QR in trên vé bằng camera -> tự tìm hồ sơ theo số vé */}
                          <TicketQrScanControl
                            showInput={false}
                            onDecoded={handleTicketDecoded}
                            disabled={!PageActionPermission.canEnable(location.pathname, 'input-scan-ticket-or-id')}
                          />
                        </form>
                      </Col>
                    )}
                    <Col md={3}>
                      {isPostAudit ? (
                        <>
                          <Label className="form-label">{st('giai_doan_label')}</Label>
                          <Input
                            type="select"
                            className="form-select"
                            value={reviewStage}
                            onChange={(e) => setReviewStage(e.target.value as ReviewStageFilter)}
                          >
                            {(Object.keys(REVIEW_STAGE_LABELS) as ReviewStageFilter[]).map((value) => (
                              <option key={value} value={value}>
                                {REVIEW_STAGE_LABELS[value]}
                              </option>
                            ))}
                          </Input>
                        </>
                      ) : (
                        <>
                          <Label className="form-label">{st('trang_thai_label')}</Label>
                          <Input
                            type="select"
                            className="form-select"
                            value={filters.status || ''}
                            onChange={(e) => dispatch(setStatusFilter(e.target.value || undefined))}
                          >
                            <option value="">{st('tat_ca_trang_thai_option')}</option>
                            {SOURCE_STATUS_OPTIONS[source].map((value) => (
                              <option key={value} value={value}>
                                {CLAIM_DOSSIER_STATUS_LABELS[value]}
                              </option>
                            ))}
                          </Input>
                        </>
                      )}
                    </Col>
                  </Row>
                  <ClaimDossierTable
                    dossiers={dossiers}
                    loading={loading}
                    approvingId={approvingId}
                    taxConfig={taxConfig}
                    onApprove={handleApprove}
                    onCancel={setPendingCancel}
                    onView={handleView}
                    onAppraise={(dossier) => setPendingReview({ dossier, reviewType: 'Appraisal' })}
                    onPostAudit={(dossier) => setPendingReview({ dossier, reviewType: 'PostAudit' })}
                    onViewAgentPayout={handleViewAgentPayout}
                    page={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    agentMap={agentMap}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="claim-dossier-business-note">
            <CardBody>
              <h6 className="text-danger mb-2">
                <i className="ri-information-line align-bottom me-1"></i>{' '}
                {isPostAudit ? st('nghiep_vu_hau_kiem_text') : st('nghiep_vu_tham_dinh_text')}
              </h6>
              {isPostAudit ? (
                <ul className="mb-0 ps-3" style={{ fontSize: 13.5, lineHeight: 1.7 }}>
                  <li>
                    {st('danh_sach_chi_gom_ket_qua_text')} <b>{st('tham_dinh_phe_duyet_text')}</b>.
                  </li>
                  <li>
                    {st('hau_kiem_de_soat_lai_lan_text')} <b>{st('tu_choi_text')}</b>{st('cong_no_tra_thuong_da_tinh_text')}
                  </li>
                </ul>
              ) : (
                <ul className="mb-0 ps-3" style={{ fontSize: 13.5, lineHeight: 1.7 }}>
                  <li>{st('dam_bao_ve_trung_thuong_hop_text')}</li>
                  <li>
                    {st('tu_dong_khau_tru_text')} <b>{formatTaxRate(taxConfig)} {st('thue_tncn_text')}</b> {st('cho_phan_gia_tri_vuot_text')}{' '}
                    {formatTaxThreshold(taxConfig)}.
                  </li>
                  <li>{st('sau_khi_ky_duyet_ho_so_text')}</li>
                </ul>
              )}
              <hr className="my-2" />
              <small className="text-muted">
                {st('chu_giai_hs_ho_so_cccd_hint')}
              </small>
            </CardBody>
          </Card>
        </Container>
      </div>

      <CreateClaimDossierModal
        isOpen={modalState.create}
        onClose={() => dispatch(closeModal('create'))}
        onSuccess={handleRefresh}
        taxConfig={taxConfig}
      />

      <DossierDecisionModal
        isOpen={!!pendingReview}
        title={
          pendingReview?.reviewType === 'Appraisal'
            ? st('tham_dinh_ho_so_dossier_code_text', { dossier_code: pendingReview.dossier.dossierCode })
            : st('hau_kiem_ho_so_dossier_code_text', { dossier_code: pendingReview?.dossier.dossierCode })
        }
        submitting={reviewSubmitting}
        onSubmit={handleSubmitReview}
        onCancel={() => setPendingReview(null)}
      />

      <AgentPayoutInfoModal
        isOpen={agentPayoutOpen}
        loading={agentPayoutLoading}
        info={agentPayoutInfo}
        onClose={() => setAgentPayoutOpen(false)}
      />

      <Modal isOpen={!!pendingCancel} toggle={() => setPendingCancel(null)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-close-circle-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{st('huy_ho_so_nay_title')}</h4>
              <p className="text-muted mx-4 mb-0">
                {st('ban_co_chac_chan_muon_huy_text')} <b>{pendingCancel?.dossierCode}</b> {st('khong_text')}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" className="w-sm" onClick={() => setPendingCancel(null)}>
              {st('dong_button')}
            </Button>
            {PageActionPermission.canDisplay(location.pathname, 'button-cancel-confirm') && (
              <Button
                color="danger"
                className="w-sm"
                data-action-code="button-cancel-confirm"
                data-action-name="Xác nhận hủy hồ sơ"
                onClick={handleConfirmCancel}
              >
                {st('co_huy_ho_so_button')}
              </Button>
            )}
          </div>
        </ModalBody>
      </Modal>

      {modalState.detail && (
        <Modal isOpen toggle={() => dispatch(closeModal('detail'))} centered size="lg">
          {detailLoading || !selectedDossier ? (
            <ModalBody className="text-center py-5">
              <span className="spinner-border text-primary" />
              <p className="text-muted mt-3 mb-0">{st('dang_tai_chi_tiet_ho_so_text')}</p>
            </ModalBody>
          ) : (
          <ModalBody>
            <h5 className="mb-3">{st('chi_tiet_ho_so_title')} {selectedDossier.dossierCode}</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="p-3 rounded border h-100">
                  <h6 className="text-muted fs-12 fw-bold mb-3">{st('thong_tin_ve_va_trung_thuong_title')}</h6>
                  <table className="table table-borderless table-sm mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted ps-0" style={{ width: 110 }}>
                          {st('ky_ve_text')}
                        </td>
                        <td className="pe-0">{selectedDossier.issuancePlanCode || selectedDossier.issuancePlanId}</td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('ma_ve_text')}</td>
                        <td className="pe-0">
                          {selectedDossier.symbol}-{selectedDossier.ticketNumber}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('giai_trung_text')}</td>
                        <td className="pe-0">
                          <div className="d-flex flex-wrap gap-1">
                            {selectedDossier.prizes && selectedDossier.prizes.length > 0 ? (
                              selectedDossier.prizes.map((p: any) => (
                                <Badge key={p.prizeTierCode} color="info" className="fs-12">
                                  {p.prizeTierDisplayName}
                                </Badge>
                              ))
                            ) : (
                              <Badge color="info" className="fs-12">
                                {selectedDossier.prizeTierDisplayName || selectedDossier.prizeTierCode}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('nguoi_linh_text')}</td>
                        <td className="pe-0">
                          {selectedDossier.claimantName} {st('cccd_text_2')} {selectedDossier.claimantIdNumber}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('tai_khoan_nhan_tien_text')}</td>
                        <td className="pe-0">
                          {selectedDossier.bankName} – {selectedDossier.bankAccountName} – {selectedDossier.bankAccountNumber}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 rounded border h-100">
                  <h6 className="text-muted fs-12 fw-bold mb-3">{st('thong_tin_tai_chinh_title')}</h6>
                  {(() => {
                    // Mục 8 (README.md §7) — hồ sơ chưa ký duyệt: hiển thị Thuế/Thực lĩnh tính lại theo
                    // cấu hình HIỆN TẠI (khi ký duyệt backend ghi đè đúng số này). Hồ sơ đã chốt: giữ số đã lưu.
                    const recompute = isTaxRecomputable(selectedDossier.status);
                    const { tax, net } = recompute
                      ? computeWithholdingTax(selectedDossier.prizeAmount, taxConfig)
                      : { tax: selectedDossier.withholdingTax, net: selectedDossier.netAmount };
                    return (
                      <table className="table table-borderless table-sm mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted ps-0" style={{ width: 110 }}>
                              {st('tien_thuong_text')}
                            </td>
                            <td className="pe-0 text-end">
                              {Number(selectedDossier.prizeAmount).toLocaleString('vi-VN')} {st('d_text')}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted ps-0">
                              {st('thue_tncn_text_2')}{formatTaxRate(taxConfig)})
                              {recompute && tax !== selectedDossier.withholdingTax && (
                                <div className="text-muted fs-11">{st('tinh_theo_cau_hinh_hien_tai_text')}</div>
                              )}
                            </td>
                            <td className="pe-0 text-end">
                              -{Number(tax).toLocaleString('vi-VN')} {st('d_text')}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted ps-0 fw-bold">{st('thuc_linh_text')}</td>
                            <td className="pe-0 text-end fw-bold fs-15">
                              {Number(net).toLocaleString('vi-VN')} {st('d_text')}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
              {/* Vé có thể trúng nhiều giải cùng lúc — liệt kê chi tiết từng giải, "Tiền thưởng" ở card Thông
                  tin tài chính là tổng của các dòng này. */}
              {selectedDossier.prizes && selectedDossier.prizes.length > 1 && (
                <div className="col-12">
                  <div className="p-3 rounded border">
                    <h6 className="text-muted fs-12 fw-bold mb-3">{st('chi_tiet_cac_giai_trung_title')}</h6>
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        {selectedDossier.prizes.map((p: any) => (
                          <tr key={p.prizeTierCode}>
                            <td className="ps-0">{p.prizeTierDisplayName}</td>
                            <td className="pe-0 text-end">{Number(p.prizeAmount).toLocaleString('vi-VN')} {st('d_text')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(selectedDossier.idCardFrontImageUrl || selectedDossier.idCardBackImageUrl) && (
                <div className="col-12">
                  <div className="p-3 rounded border">
                    <h6 className="text-muted fs-12 fw-bold mb-3">{st('anh_cccd_title')}</h6>
                    <div className="d-flex gap-3 flex-wrap">
                      {selectedDossier.idCardFrontImageUrl && (
                        <a href={selectedDossier.idCardFrontImageUrl} target="_blank" rel="noreferrer">
                          <img
                            src={selectedDossier.idCardFrontImageUrl}
                            alt={st('anh_cccd_mat_truoc_alt')}
                            className="rounded border"
                            style={{ height: 130, objectFit: 'cover' }}
                          />
                          <div className="text-center text-muted fs-12 mt-1">{st('mat_truoc_text')}</div>
                        </a>
                      )}
                      {selectedDossier.idCardBackImageUrl && (
                        <a href={selectedDossier.idCardBackImageUrl} target="_blank" rel="noreferrer">
                          <img
                            src={selectedDossier.idCardBackImageUrl}
                            alt={st('anh_cccd_mat_sau_alt')}
                            className="rounded border"
                            style={{ height: 130, objectFit: 'cover' }}
                          />
                          <div className="text-center text-muted fs-12 mt-1">{st('mat_sau_text')}</div>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="col-12">
                <div className="p-3 rounded border">
                  <h6 className="text-muted fs-12 fw-bold mb-3">{st('trang_thai_va_phe_duyet_title')}</h6>
                  <table className="table table-borderless table-sm mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted ps-0" style={{ width: 110 }}>
                          {st('trang_thai_label')}
                        </td>
                        <td className="pe-0">
                          <Badge color={(CLAIM_DOSSIER_STATUS_BADGE as any)[selectedDossier.status]}>
                            {(CLAIM_DOSSIER_STATUS_LABELS as any)[selectedDossier.status]}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('trang_thai_chi_tra_text')}</td>
                        <td className="pe-0">
                          <Badge color={(CLAIM_DOSSIER_PAYMENT_STATUS_BADGE as any)[selectedDossier.paymentStatus]}>
                            {(CLAIM_DOSSIER_PAYMENT_STATUS_LABELS as any)[selectedDossier.paymentStatus]}
                          </Badge>
                        </td>
                      </tr>
                      {(selectedDossier.createdByName || selectedDossier.createdBy) && (
                        <tr>
                          <td className="text-muted ps-0">{st('nguoi_lap_text')}</td>
                          <td className="pe-0">
                            {selectedDossier.createdByName || selectedDossier.createdBy}
                            {selectedDossier.createdAt
                              ? ` — ${new Date(selectedDossier.createdAt).toLocaleString('vi-VN')}`
                              : ''}
                          </td>
                        </tr>
                      )}
                      {selectedDossier.agentId && (
                        <tr>
                          <td className="text-muted ps-0">{st('dai_ly_tra_thuong_text')}</td>
                          <td className="pe-0">
                            {(() => {
                              const agent = agentMap[Number(selectedDossier.agentId)];
                              const name = selectedDossier.agentName || agent?.name;
                              const code = selectedDossier.agentCode || agent?.code;
                              return name
                                ? `${name}${code ? ` (${code})` : ''}`
                                : st('dl_agent_id_text', { agent_id: selectedDossier.agentId });
                            })()}
                          </td>
                        </tr>
                      )}
                      {selectedDossier.status === 'AgentPaid' && (
                        <>
                          <tr>
                            <td className="text-muted ps-0">{st('ghi_chu_text')}</td>
                            <td className="pe-0 text-info">
                              {st('dai_ly_tu_lap_va_da_text')}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted ps-0">{st('tham_dinh_button')}</td>
                            <td className="pe-0">
                              {selectedDossier.appraisalDecision ? (
                                <>
                                  <Badge color={CLAIM_DOSSIER_REVIEW_DECISION_BADGE[selectedDossier.appraisalDecision as ClaimDossierReviewDecision]}>
                                    {CLAIM_DOSSIER_REVIEW_DECISION_LABELS[selectedDossier.appraisalDecision as ClaimDossierReviewDecision]}
                                  </Badge>{' '}
                                  <span className="text-muted fs-12">
                                    {selectedDossier.appraisalReviewedByName || selectedDossier.appraisalReviewedBy}
                                    {selectedDossier.appraisalReviewedAt
                                      ? ` — ${new Date(selectedDossier.appraisalReviewedAt).toLocaleString('vi-VN')}`
                                      : ''}
                                  </span>
                                  <ReviewCommentBox comment={selectedDossier.appraisalComment} />
                                </>
                              ) : (
                                <span className="text-muted">{st('chua_tham_dinh_text')}</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted ps-0">{st('hau_kiem_button')}</td>
                            <td className="pe-0">
                              {selectedDossier.postAuditDecision ? (
                                <>
                                  <Badge color={CLAIM_DOSSIER_REVIEW_DECISION_BADGE[selectedDossier.postAuditDecision as ClaimDossierReviewDecision]}>
                                    {CLAIM_DOSSIER_REVIEW_DECISION_LABELS[selectedDossier.postAuditDecision as ClaimDossierReviewDecision]}
                                  </Badge>{' '}
                                  <span className="text-muted fs-12">
                                    {selectedDossier.postAuditReviewedByName || selectedDossier.postAuditReviewedBy}
                                    {selectedDossier.postAuditReviewedAt
                                      ? ` — ${new Date(selectedDossier.postAuditReviewedAt).toLocaleString('vi-VN')}`
                                      : ''}
                                  </span>
                                  <ReviewCommentBox comment={selectedDossier.postAuditComment} />
                                </>
                              ) : (
                                <span className="text-muted">
                                  {selectedDossier.appraisalDecision === 'PheDuyet' ? st('chua_hau_kiem_text') : st('cho_tham_dinh_truoc_text')}
                                </span>
                              )}
                            </td>
                          </tr>
                        </>
                      )}
                      {selectedDossier.status === 'Verified' && (
                        <tr>
                          <td className="text-muted ps-0">{st('nguoi_duyet_text')}</td>
                          <td className="pe-0">
                            {selectedDossier.verifiedByName || selectedDossier.verifiedBy} —{' '}
                            {selectedDossier.verifiedAt
                              ? new Date(selectedDossier.verifiedAt).toLocaleString('vi-VN')
                              : ''}
                          </td>
                        </tr>
                      )}
                      {selectedDossier.status === 'Cancelled' && selectedDossier.cancelReason && (
                        <tr>
                          <td className="text-muted ps-0">{st('ly_do_huy_text')}</td>
                          <td className="pe-0">{selectedDossier.cancelReason}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </ModalBody>
          )}
          <ModalFooter>
            <Button color="light" onClick={() => dispatch(closeModal('detail'))}>
              {st('dong_button')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ClaimDossierPage;
